// The WebAudio engine (audio §5, §7.2): context lifecycle, unlock on the first user gesture,
// one-time offline pre-render of every recipe, voice pooling and limits, the master/group gain
// chain, settings gating, tab-visibility suspend and drop-in file overrides.
//
// Graph: voice → group gain (game | menu) → master gain (`settings.volume`) → compressor →
// destination. A voice is one AudioBufferSourceNode playing a pre-rendered buffer (drop-in files
// add one per-voice GainNode that matches their −3 dBFS normalisation to the slot's level).
// Before a slot's buffer is ready, the recipe is synthesised live into the realtime context.
//
// Rules (audio §5): at most VOICE_CAP concurrent voices, a play beyond it is dropped (never
// stolen); per-slot guards in GUARDS (concurrent voices and minimum start gap); no pitch, pan
// or randomisation (the cash-out tick's playback rate follows the hold progress, which is
// deterministic). Plays before the unlock, while the tab is hidden or the context is suspended,
// with the slot's group off or the volume at 0 are dropped, never queued. Every WebAudio call
// is guarded: a failure disables audio for the session and never throws into the game loop.
//
// `play(slot)` is the direct entry. Once `attachAudioTriggers` (audio/triggers.ts) drives a slot
// from the client bus, direct `play` calls for that slot are ignored, so app.ts's legacy calls
// cannot double a sound; the bus path uses `trigger`, which skips that check.
//
// Implementer: client-audio (lane A). Consumers: app.ts (via the `Sfx` re-export in sfx.ts),
// audio/triggers.ts, audio/dropins.ts.

import {
  dbGain,
  RECIPES,
  RENDER_TAIL_MS,
  normalizePeak,
  SOUND_SLOTS,
  type SoundGroup,
  type SoundSlot,
  whiteNoise,
} from './recipes.js';

/** Global concurrent-voice cap (audio §5). */
export const VOICE_CAP = 32;

export interface SlotGuard {
  /** Concurrent voices of this slot. */
  readonly maxVoices?: number;
  /** Minimum time between two starts of this slot (ms). */
  readonly minGapMs?: number;
}

/** Per-slot limits (audio §5; the money/notice rows are ball.io extensions). */
export const GUARDS: Readonly<Partial<Record<SoundSlot, SlotGuard>>> = Object.freeze({
  eat: { maxVoices: 6, minGapMs: 30 },
  pop: { minGapMs: 50 },
  shoot: { minGapMs: 60 },
  moneyGain: { maxVoices: 2, minGapMs: 90 },
  cashTick: { minGapMs: 150 },
  cashStart: { minGapMs: 150 },
  notice: { minGapMs: 250 },
  error: { minGapMs: 250 },
  // Territory arena (territory-audio.md §4).
  capture: { minGapMs: 80 },
  captureMid: { minGapMs: 80 },
  captureBig: { minGapMs: 80 },
  kill: { maxVoices: 2, minGapMs: 120 },
  shatter: { maxVoices: 2, minGapMs: 120 },
  spill: { minGapMs: 150 },
  collect: { minGapMs: 40 },
  homeAlarm: { minGapMs: 3000 },
});

/** Drop-in files are normalised to −3 dBFS (art-assets §8). */
export const DROP_IN_PEAK_DB = -3;
/** The master gain glides to a new volume with this time constant (audio §6: no zipper noise). */
export const VOLUME_TIME_CONSTANT = 0.015;
/** A voice whose `onended` never arrived is considered done this long after its end (s). */
const VOICE_SLACK_SEC = 0.25;

/** The settings the engine reads (settings/store.ts `Settings` satisfies it). */
export interface AudioSettings {
  /** Gates every `game` slot (default on, owner decision D-A1). */
  readonly gameSounds: boolean;
  /** Gates every `menu` slot. */
  readonly menuSounds: boolean;
  /** Master volume 0–1; 0 behaves as mute (no voices started). */
  readonly volume: number;
  /** Optional per-group levels 0–1 (default 1); not persisted by the settings store yet. */
  readonly gameVolume?: number;
  readonly menuVolume?: number;
}

/** Whether `slot` may start under `settings` (group switch, non-zero master and group level). */
export function soundAllowed(slot: SoundSlot, settings: AudioSettings): boolean {
  if (!(settings.volume > 0)) return false;
  if (RECIPES[slot].group === 'game')
    return settings.gameSounds && groupLevel(settings.gameVolume) > 0;
  return settings.menuSounds && groupLevel(settings.menuVolume) > 0;
}

function groupLevel(v: number | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
}

function clampVolume(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
}

export interface PlayOptions {
  /** Playback-rate multiplier (pitch and speed); 1 = as rendered. */
  readonly rate?: number;
  /** Start this long after now (ms), e.g. to layer a sound just after another. */
  readonly delayMs?: number;
}

/** Anything that can play slots (the engine, or a spy in tests). */
export interface SoundPlayer {
  play(slot: SoundSlot, nowMs?: number, opts?: PlayOptions): boolean;
  /** Plays a bus-driven slot (skips the legacy-call filter). */
  trigger(slot: SoundSlot, nowMs?: number, opts?: PlayOptions): boolean;
  /** Marks the slots the client bus drives (legacy direct `play` calls for them are ignored). */
  setBusDriven(slots: Iterable<SoundSlot>): void;
}

type AudioContextCtor = new (opts?: AudioContextOptions) => AudioContext;
type OfflineAudioContextCtor = new (
  channels: number,
  length: number,
  sampleRate: number,
) => OfflineAudioContext;

/** The bits of `document` the engine uses (tab visibility). */
export interface VisibilitySource {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', fn: () => void): void;
  removeEventListener(type: 'visibilitychange', fn: () => void): void;
}

/** A target for the unlock gesture listeners (window). */
export interface GestureTarget {
  addEventListener(type: string, fn: () => void, capture?: boolean): void;
  removeEventListener(type: string, fn: () => void, capture?: boolean): void;
}

export interface AudioEngineOptions {
  readonly settings: () => AudioSettings;
  /** Undefined = `globalThis.AudioContext` (or the webkit-prefixed one); null = no audio. */
  readonly AudioContextImpl?: AudioContextCtor | null;
  /** Undefined = `globalThis.OfflineAudioContext`; null = never pre-render (live synthesis). */
  readonly OfflineAudioContextImpl?: OfflineAudioContextCtor | null;
  /** Undefined = `globalThis.document`; null = ignore tab visibility. */
  readonly visibility?: VisibilitySource | null;
  /** Clock for the guards, ms (default `performance.now`). */
  readonly now?: () => number;
  /** Where failures are reported once (default `console.warn`). */
  readonly warn?: (msg: string, err?: unknown) => void;
}

/** One pooled voice record (no allocation per play beyond the WebAudio nodes themselves). */
interface Voice {
  slot: SoundSlot | null;
  /** Context time the voice ends at (s). */
  endAt: number;
  src: AudioBufferSourceNode | null;
  readonly onEnded: (ev: Event) => void;
}

/** The gesture events that unlock audio (audio §7.2; iOS needs `touchend`). */
export const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'keydown', 'touchend', 'click'] as const;

/**
 * Renders `slot` once in an OfflineAudioContext (mono, `sampleRate`, length + RENDER_TAIL_MS) and
 * normalises its peak to the recipe's `targetDb`.
 */
export async function prerenderSlot(
  slot: SoundSlot,
  Offline: OfflineAudioContextCtor,
  sampleRate: number,
  noise: AudioBuffer | null,
): Promise<AudioBuffer> {
  const r = RECIPES[slot];
  const length = Math.ceil(((r.lengthMs + RENDER_TAIL_MS) / 1000) * sampleRate);
  const off = new Offline(1, length, sampleRate);
  r.render({ ctx: off, out: off.destination, t0: 0, noise });
  const buf = await off.startRendering();
  normalizePeak(buf.getChannelData(0), r.targetDb);
  return buf;
}

export class AudioEngine implements SoundPlayer {
  /** Voices started so far (the "sounds off ⇒ 0 plays" spy). */
  plays = 0;
  /** Plays dropped by a cap or guard (diagnostics). */
  dropped = 0;

  private readonly opts: AudioEngineOptions;
  private readonly now: () => number;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private game: GainNode | null = null;
  private menu: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private disabled = false;
  private warned = false;
  private hidden = false;
  private visibilityBound = false;
  private appliedVolume = -1;
  private appliedGame = -1;
  private appliedMenu = -1;
  private readonly buffers = new Map<SoundSlot, AudioBuffer>();
  private readonly dropIns = new Map<SoundSlot, AudioBuffer>();
  private readonly pendingDropIns = new Map<SoundSlot, ArrayBuffer>();
  private readonly lastStart = new Map<SoundSlot, number>();
  private readonly busDriven = new Set<SoundSlot>();
  private readonly voices: Voice[] = [];
  private prerenderDone: Promise<void> = Promise.resolve();

  constructor(opts: AudioEngineOptions) {
    this.opts = opts;
    this.now = opts.now ?? (() => performance.now());
    for (let i = 0; i < VOICE_CAP; i++) this.voices.push(this.newVoice());
  }

  /** The master gain node (null until unlocked). */
  get masterGain(): GainNode | null {
    return this.master;
  }

  /** The output compressor (null until unlocked). */
  get compressorNode(): DynamicsCompressorNode | null {
    return this.compressor;
  }

  /** The game / menu group gain (null until unlocked). */
  groupGain(group: SoundGroup): GainNode | null {
    return group === 'game' ? this.game : this.menu;
  }

  /** The realtime context (null until unlocked, or after a failure / close). */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /** True once a failure turned audio off for the session. */
  get isDisabled(): boolean {
    return this.disabled;
  }

  /** Resolves when the pre-render started by the unlock has finished (tests, diagnostics). */
  whenReady(): Promise<void> {
    return this.prerenderDone;
  }

  /** Whether `slot` has a pre-rendered (or drop-in) buffer. */
  hasBuffer(slot: SoundSlot): boolean {
    return this.dropIns.has(slot) || this.buffers.has(slot);
  }

  /** Concurrent live voices (all slots, or one). */
  liveVoices(slot?: SoundSlot): number {
    const t = this.ctx ? this.safeTime(this.ctx) : 0;
    let n = 0;
    for (const v of this.voices)
      if (this.isLive(v, t) && (slot === undefined || v.slot === slot)) n++;
    return n;
  }

  /**
   * Creates (once) and resumes the AudioContext; call from a user gesture. Safe to call
   * repeatedly: later calls only retry `resume()` while the context is suspended.
   */
  unlock(): void {
    if (this.disabled) return;
    try {
      if (!this.ctx) {
        const Ctor = this.resolveContextCtor();
        if (!Ctor) return;
        const ctx = new Ctor({ latencyHint: 'interactive' });
        this.ctx = ctx;
        this.buildGraph(ctx);
        this.bindVisibility();
        this.decodePendingDropIns(ctx);
        this.prerenderDone = this.prerender(ctx);
      }
      const ctx = this.ctx;
      const state = ctx.state as string;
      if ((state === 'suspended' || state === 'interrupted') && !this.hidden) {
        void ctx.resume().catch(() => undefined);
      }
    } catch (err) {
      this.fail('could not start audio', err);
    }
  }

  /**
   * Adds capture-phase unlock listeners for every gesture in UNLOCK_EVENTS to `target`
   * (window); returns the disposer.
   */
  attachUnlock(target: GestureTarget): () => void {
    const fn = (): void => this.unlock();
    for (const type of UNLOCK_EVENTS) target.addEventListener(type, fn, true);
    return () => {
      for (const type of UNLOCK_EVENTS) target.removeEventListener(type, fn, true);
    };
  }

  /** Re-reads the volume settings into the master and group gains (glides, no zipper noise). */
  applyVolume(): void {
    const s = this.readSettings();
    if (s) this.syncVolumes(s);
  }

  /** A decoded drop-in file replaces the synthesised sound for `slot` (null = back to synth). */
  setBuffer(slot: SoundSlot, buffer: AudioBuffer | null): void {
    if (buffer) this.dropIns.set(slot, buffer);
    else this.dropIns.delete(slot);
  }

  /**
   * Encoded drop-in file bytes for `slot` (audio/dropins.ts): decoded now if the context exists,
   * else on unlock. A file that does not decode keeps the synthesised sound.
   */
  setDropInData(slot: SoundSlot, data: ArrayBuffer): void {
    if (this.ctx) this.decodeDropIn(this.ctx, slot, data);
    else this.pendingDropIns.set(slot, data);
  }

  setBusDriven(slots: Iterable<SoundSlot>): void {
    this.busDriven.clear();
    for (const s of slots) this.busDriven.add(s);
  }

  /** Direct play (app.ts legacy calls, countdown/zone): ignored for slots the bus drives. */
  play(slot: SoundSlot, nowMs: number = this.now(), opts?: PlayOptions): boolean {
    if (this.busDriven.has(slot)) return false;
    return this.trigger(slot, nowMs, opts);
  }

  /** Starts one voice of `slot` if every gate, cap and guard allows it; true when started. */
  trigger(slot: SoundSlot, nowMs: number = this.now(), opts?: PlayOptions): boolean {
    if (this.disabled) return false;
    const recipe = RECIPES[slot];
    if (!recipe) return false;
    const settings = this.readSettings();
    if (!settings || !soundAllowed(slot, settings)) return false;
    const ctx = this.ctx;
    const out = recipe.group === 'game' ? this.game : this.menu;
    if (!ctx || !out || this.hidden || ctx.state !== 'running') return false;

    const guard = GUARDS[slot];
    if (guard?.minGapMs !== undefined) {
      const last = this.lastStart.get(slot);
      if (last !== undefined && nowMs - last < guard.minGapMs) {
        this.dropped++;
        return false;
      }
    }
    const t = this.safeTime(ctx);
    let total = 0;
    let same = 0;
    let free: Voice | null = null;
    for (const v of this.voices) {
      if (this.isLive(v, t)) {
        total++;
        if (v.slot === slot) same++;
      } else if (!free) {
        free = v;
      }
    }
    if (
      total >= VOICE_CAP ||
      !free ||
      (guard?.maxVoices !== undefined && same >= guard.maxVoices)
    ) {
      this.dropped++;
      return false;
    }

    try {
      this.syncVolumes(settings);
      const rate =
        opts?.rate !== undefined && Number.isFinite(opts.rate) && opts.rate > 0 ? opts.rate : 1;
      const when = t + Math.max(0, opts?.delayMs ?? 0) / 1000;
      const dropIn = this.dropIns.get(slot);
      const buffer = dropIn ?? this.buffers.get(slot);
      free.slot = slot;
      if (buffer) {
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        if (rate !== 1) src.playbackRate.value = rate;
        if (dropIn) {
          const g = ctx.createGain();
          g.gain.value = dbGain(recipe.targetDb - DROP_IN_PEAK_DB);
          src.connect(g);
          g.connect(out);
        } else {
          src.connect(out);
        }
        src.onended = free.onEnded;
        free.src = src;
        free.endAt = when + buffer.duration / rate;
        src.start(when);
      } else {
        // Not pre-rendered yet: synthesise live (same recipe, unnormalised).
        free.src = null;
        free.endAt = when + recipe.lengthMs / 1000;
        recipe.render({ ctx, out, t0: when, noise: this.noise });
      }
    } catch (err) {
      free.slot = null;
      free.src = null;
      this.fail(`could not play "${slot}"`, err);
      return false;
    }
    this.lastStart.set(slot, nowMs);
    this.plays++;
    return true;
  }

  /** Closes the AudioContext (app stop). A later `unlock` starts a fresh one. */
  close(): void {
    const ctx = this.ctx;
    this.unbindVisibility();
    this.resetGraph();
    for (const v of this.voices) {
      v.slot = null;
      v.src = null;
    }
    if (ctx) {
      try {
        void ctx.close().catch(() => undefined);
      } catch {
        // Already closed.
      }
    }
  }

  // --- internals ------------------------------------------------------------------------------

  private newVoice(): Voice {
    const v: Voice = {
      slot: null,
      endAt: 0,
      src: null,
      onEnded: (ev: Event) => {
        const target = (ev as Event | undefined)?.target ?? null;
        if (target !== null && target !== v.src) return; // a stale source of a recycled record
        v.slot = null;
        v.src = null;
      },
    };
    return v;
  }

  private isLive(v: Voice, t: number): boolean {
    return v.slot !== null && t < v.endAt + VOICE_SLACK_SEC;
  }

  private safeTime(ctx: BaseAudioContext): number {
    const t = ctx.currentTime;
    return Number.isFinite(t) ? t : 0;
  }

  private readSettings(): AudioSettings | null {
    try {
      return this.opts.settings();
    } catch {
      return null;
    }
  }

  private resolveContextCtor(): AudioContextCtor | null {
    if (this.opts.AudioContextImpl !== undefined) return this.opts.AudioContextImpl;
    const g = globalThis as {
      AudioContext?: AudioContextCtor;
      webkitAudioContext?: AudioContextCtor;
    };
    return g.AudioContext ?? g.webkitAudioContext ?? null;
  }

  private resolveOfflineCtor(): OfflineAudioContextCtor | null {
    if (this.opts.OfflineAudioContextImpl !== undefined) return this.opts.OfflineAudioContextImpl;
    const g = globalThis as {
      OfflineAudioContext?: OfflineAudioContextCtor;
      webkitOfflineAudioContext?: OfflineAudioContextCtor;
    };
    return g.OfflineAudioContext ?? g.webkitOfflineAudioContext ?? null;
  }

  private buildGraph(ctx: AudioContext): void {
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -10;
    comp.knee.value = 6;
    comp.ratio.value = 4;
    comp.attack.value = 0.003;
    comp.release.value = 0.12;
    comp.connect(ctx.destination);
    const master = ctx.createGain();
    master.connect(comp);
    const game = ctx.createGain();
    game.connect(master);
    const menu = ctx.createGain();
    menu.connect(master);
    this.compressor = comp;
    this.master = master;
    this.game = game;
    this.menu = menu;
    this.appliedVolume = this.appliedGame = this.appliedMenu = -1;
    const s = this.readSettings();
    if (s) this.syncVolumes(s, true);

    const sr = ctx.sampleRate > 0 ? ctx.sampleRate : 48000;
    const data = whiteNoise(sr);
    const noise = ctx.createBuffer(1, data.length, sr);
    noise.getChannelData(0).set(data);
    this.noise = noise;
  }

  private resetGraph(): void {
    this.ctx = null;
    this.master = null;
    this.compressor = null;
    this.game = null;
    this.menu = null;
    this.noise = null;
    this.buffers.clear();
    this.appliedVolume = this.appliedGame = this.appliedMenu = -1;
  }

  /** Applies volume changes: the first set is immediate, later ones glide. */
  private syncVolumes(s: AudioSettings, immediate = false): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const set = (node: GainNode | null, v: number, prev: number): void => {
      if (!node || v === prev) return;
      if (immediate || prev < 0) node.gain.value = v;
      else node.gain.setTargetAtTime(v, this.safeTime(ctx), VOLUME_TIME_CONSTANT);
    };
    const vol = clampVolume(s.volume);
    const game = groupLevel(s.gameVolume);
    const menu = groupLevel(s.menuVolume);
    set(this.master, vol, this.appliedVolume);
    set(this.game, game, this.appliedGame);
    set(this.menu, menu, this.appliedMenu);
    this.appliedVolume = vol;
    this.appliedGame = game;
    this.appliedMenu = menu;
  }

  private async prerender(ctx: AudioContext): Promise<void> {
    const Offline = this.resolveOfflineCtor();
    if (!Offline) return;
    const sr = ctx.sampleRate > 0 ? ctx.sampleRate : 48000;
    for (const slot of SOUND_SLOTS) {
      if (this.ctx !== ctx) return; // closed or replaced meanwhile
      try {
        const buf = await prerenderSlot(slot, Offline, sr, this.noise);
        if (this.ctx === ctx) this.buffers.set(slot, buf);
      } catch {
        // That slot keeps the live synthesis.
      }
    }
  }

  private decodePendingDropIns(ctx: AudioContext): void {
    for (const [slot, data] of this.pendingDropIns) this.decodeDropIn(ctx, slot, data);
    this.pendingDropIns.clear();
  }

  private decodeDropIn(ctx: AudioContext, slot: SoundSlot, data: ArrayBuffer): void {
    try {
      void ctx
        .decodeAudioData(data.slice(0))
        .then((buf) => {
          if (this.ctx === ctx) this.setBuffer(slot, buf);
        })
        .catch((err: unknown) =>
          this.warnOnce(
            `sound file for "${slot}" did not decode; using the synthesised sound`,
            err,
          ),
        );
    } catch (err) {
      this.warnOnce(`sound file for "${slot}" did not decode; using the synthesised sound`, err);
    }
  }

  private readonly onVisibility = (): void => {
    const vis = this.visibility();
    if (!vis) return;
    this.hidden = vis.visibilityState === 'hidden';
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      if (this.hidden) void ctx.suspend().catch(() => undefined);
      else void ctx.resume().catch(() => undefined);
    } catch {
      // Closed context: nothing to do.
    }
  };

  private visibility(): VisibilitySource | null {
    if (this.opts.visibility !== undefined) return this.opts.visibility;
    const d = (globalThis as { document?: VisibilitySource }).document;
    return d ?? null;
  }

  private bindVisibility(): void {
    const vis = this.visibility();
    if (!vis || this.visibilityBound) return;
    this.hidden = vis.visibilityState === 'hidden';
    vis.addEventListener('visibilitychange', this.onVisibility);
    this.visibilityBound = true;
  }

  private unbindVisibility(): void {
    const vis = this.visibility();
    if (vis && this.visibilityBound) vis.removeEventListener('visibilitychange', this.onVisibility);
    this.visibilityBound = false;
  }

  private warnOnce(msg: string, err?: unknown): void {
    if (this.warned) return;
    this.warned = true;
    try {
      (this.opts.warn ?? ((m: string, e?: unknown) => console.warn(`ball.io: ${m}`, e)))(msg, err);
    } catch {
      // Never let reporting break anything.
    }
  }

  /** Turns audio off for the session after a WebAudio failure. */
  private fail(msg: string, err: unknown): void {
    this.warnOnce(`${msg}; sounds are off for this session`, err);
    this.disabled = true;
    this.close();
  }
}
